import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { useConfigStore } from "@/store/config";
import { useCellGroupStore } from "@/store";
import type { CellGroupsPostPayload, CellGroupsResponse } from "@/types/api";
import { useApi, useConstants, useCookie } from "@/composables/useAppGlobals";

export function useCells() {
  const $api = useApi();
  const $const = useConstants();
  const $cookie = useCookie();

  const isBusy = ref(false);
  const isReviewed = ref(false);

  const configStore = useConfigStore();
  const cellGroupStore = useCellGroupStore();

  // expose slideInfo as a computed wrapper around the store so consumers
  // always observe the store's current object (avoids stale references)
  const { slideInfo } = storeToRefs(configStore);

  async function postCells(cellGroupPostPayload: CellGroupsPostPayload) {
    try {
      const resp = await $api.postCells(
        slideInfo.value[$const.SLIDE_KEYS.SLIDE_ID] as string,
        cellGroupPostPayload,
      );
      // write directly to the store's slideInfo to ensure reactivity for all consumers
      configStore.updateSlideInfoKey(
        $const.SLIDE_KEYS.REVIEW_TIME,
        resp.data[$const.SLIDE_KEYS.REVIEW_TIME]
      );
      configStore.updateSlideInfoKey(
        $const.SLIDE_KEYS.REVIEWER,
        $cookie.getCookie($const.COOKIES.KEY.USER_NAME)
      );
      isReviewed.value = true;
    } finally {
      isBusy.value = false;
    }
  }

  const cellBackendRequestObj = computed<CellGroupsResponse>(() => cellGroupStore.cellBackendRequestObj);

  async function save() {
    isBusy.value = true;
    const base = cellBackendRequestObj.value;
    const cellGroupPostPayload: CellGroupsPostPayload = {
      ...base,
      state: (slideInfo.value[$const.SLIDE_KEYS.STATE] as string) || "",
    };
    await postCells(cellGroupPostPayload);
  }

  function resetState() {
    configStore.resetState();
  }

  return {
    isBusy,
    isReviewed,
    slideInfo,
    cellBackendRequestObj,
    postCells,
    save,
    resetState,
  };
}

export default useCells;
